import React, {useState} from 'react';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Button, Container, Nav, Navbar as BootstrapNavbar, NavDropdown} from 'react-bootstrap';
import {Gift, Globe, LogOut, Music, Star, User} from 'react-feather';
import {logout} from '../services/auth';
import {getBasePhotoUrl} from '../services/api';


interface NavbarProps {
    isAuthenticated: boolean;
    setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
    user?: { name?: string, profile_photo?: string, profile_photo_thumbnail?: string };
}

const Navbar: React.FC<NavbarProps> = ({isAuthenticated, setIsAuthenticated, user}) => {
    const {t, i18n} = useTranslation();
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(false);

    const handleLanguageChange = (language: string) => {
        i18n.changeLanguage(language);
    };

    const getInitials = (name: string = '') => {
        return name
            .split(' ')
            .map(part => part[0]?.toUpperCase())
            .slice(0, 2)
            .join('');
    };


    const handleLogout = () => {
        logout();
        setIsAuthenticated(false);
        navigate('/');
    };

    // Hide these links when on the /dashboard page
    const location = useLocation();
    const hideNavLinks = location.pathname.startsWith('/dashboard');

    return (
        <BootstrapNavbar variant="dark" expand="lg" expanded={expanded} className="navbar-custom fixed-top">
            <Container>
                <BootstrapNavbar.Brand as={Link} to="/">
                    <img
                        src="/logo.png"
                        alt="MyWeeshy Logo"
                        className="me-2"
                        width={50}
                        style={{objectFit: "cover"}}
                    />
                    <h5 className="mb-3 d-inline align-middle">
                        MyWeeshy
                    </h5>
                </BootstrapNavbar.Brand>

                <BootstrapNavbar.Toggle
                    aria-controls="basic-navbar-nav"
                    onClick={() => setExpanded(expanded ? false : true)}
                />

                <BootstrapNavbar.Collapse id="basic-navbar-nav">
                    <Nav className="me-auto">
                        {!hideNavLinks && (
                            <>
                                <Nav.Link as={Link} to="/" onClick={() => setExpanded(false)}>
                                    {t('navbar.home')}
                                </Nav.Link>
                                <Nav.Link as={Link} to="/features" onClick={() => setExpanded(false)}>{t('navbar.features')}</Nav.Link>
                                <Nav.Link as={Link} to="/pricing" onClick={() => setExpanded(false)}>
                                    {t('navbar.pricing')}
                                </Nav.Link>
                                <Nav.Link as={Link} to="/about" onClick={() => setExpanded(false)}>{t('navbar.aboutUs')}</Nav.Link>
                                <Nav.Link as={Link} to="/contact" onClick={() => setExpanded(false)}>{t('navbar.contact')}</Nav.Link>
                            </>
                        )}


                        {isAuthenticated && (
                            <>
                                <Nav.Link as={Link} to="/dashboard" onClick={() => setExpanded(false)}>
                                    {t('navbar.dashboard')}
                                </Nav.Link>
                                {/*<Nav.Link as={Link} to="/events" onClick={() => setExpanded(false)}>*/}
                                {/*  {t('navbar.events')}*/}
                                {/*</Nav.Link>*/}
                            </>
                        )}
                    </Nav>

                    <Nav>
                        <NavDropdown
                            title={<Globe size={20}/>}
                            id="language-dropdown"
                            align="end"
                        >
                            <NavDropdown.Item onClick={() => handleLanguageChange('en')}>
                                English
                            </NavDropdown.Item>
                            <NavDropdown.Item onClick={() => handleLanguageChange('de')}>
                                Deutsch
                            </NavDropdown.Item>
                            <NavDropdown.Item onClick={() => handleLanguageChange('ru')}>
                                Русский
                            </NavDropdown.Item>
                            <NavDropdown.Item onClick={() => handleLanguageChange('az')}>
                                Azərbaycan
                            </NavDropdown.Item>
                        </NavDropdown>

                        {isAuthenticated ? (
                            <NavDropdown
                                title={
                                    <span className="d-inline-flex align-items-center gap-2">

    { user?.profile_photo ? (
        <img
            src={`${getBasePhotoUrl()}${user.profile_photo_thumbnail}`}
            alt={user.name || "User"}
            style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                objectFit: 'cover'
            }}
        />
    ) : (
    <span
        style={{
            backgroundColor: '#795EFF',
            color: 'white',
            width: 32,
            height: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.85rem',
            fontWeight: 600,
        }}
    >
      {getInitials(user?.name)}
    </span>)}
    <span style={{fontSize: '0.9rem', color: '#fff'}}>{user?.name}</span>
  </span>
                                }


                                id="user-dropdown"
                                align="end"
                            >
                                <NavDropdown.Item as={Link} to="/dashboard" onClick={() => setExpanded(false)}>
                                    <Music size={16} className="me-2"/>
                                    {t('navbar.dashboard')}
                                </NavDropdown.Item>

                                <NavDropdown.Item as={Link} to="/settings" onClick={() => setExpanded(false)}>
                                    <User size={16} className="me-2 text-secondary"/>
                                    {t('navbar.profile')}
                                </NavDropdown.Item>

                                <NavDropdown.Item as={Link} to="/subscription" onClick={() => setExpanded(false)}>
                                    <Star size={16} className="me-2 text-warning"/>
                                    {t('navbar.subscription')}
                                </NavDropdown.Item>

                                <NavDropdown.Item as={Link} to="/referral" onClick={() => setExpanded(false)}>
                                    <Gift size={16} className="me-2 text-success"/>
                                    Refer and Earn
                                </NavDropdown.Item>

                                <NavDropdown.Divider/>

                                <NavDropdown.Item onClick={() => {
                                    handleLogout();
                                    setExpanded(false);
                                }}>
                                    <LogOut size={16} className="me-2 text-danger"/>
                                    {t('navbar.logout')}
                                </NavDropdown.Item>
                            </NavDropdown>

                        ) : (
                            <>
                                <Link to="/login" onClick={() => setExpanded(false)}>
                                    <Button variant="outline-light" className="me-2">
                                        {t('navbar.login')}
                                    </Button>
                                </Link>

                                <Link to="/signup" onClick={() => setExpanded(false)}>
                                    <Button style={{backgroundColor: "#795EFF"}}>
                                        {t('navbar.signup')}
                                    </Button>
                                </Link>
                            </>
                        )}
                    </Nav>
                </BootstrapNavbar.Collapse>
            </Container>
        </BootstrapNavbar>
    );
};

export default Navbar;